import { DragAndDrop } from 'components/MenuMakeImport/DragAndDrop';
import { Box, Button, Link, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { ErrorTable, ErrorInterface } from 'components/MenuMakeImport/ErrorTable';
import { useTranslation, Trans } from 'react-i18next';

import { FeedbackBanner } from '@experimental-components/FeedbackBanner';

import { DownloadTemplateOptions } from 'components/MenuMakeImport/types';
import { useDownloadTemplate, useGetExistsMenu } from 'components/MenuMakeImport/hooks';
import { useNotification } from '@design-system/Notification/useNotification';
import { ButtonDownload } from '@experimental-components/ButtonDownload';

import { DefaultIcon, ErrorIcon, SuccessIcon } from './DragAndDropIcon';

export const UploadStep = ({ onSuccess }: { onSuccess: (excelFile: File) => void }) => {
  const [isError, setIsError] = useState(false);
  const [errorList, setErrorList] = useState<ErrorInterface[]>([]);
  const { t } = useTranslation();
  const [title, setTitle] = useState(t('menuMaker:importMenu.dragAndDrop.title_default') as string);
  const [description, setDescription] = useState(t('menuMaker:importMenu.dragAndDrop.description_default') as string);
  const [icon, setIcon] = useState<React.ReactNode>(DefaultIcon);
  const [isSuccess, setIsSuccess] = useState(false);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const { mutate: downloadTemplate } = useDownloadTemplate();
  const { setNotification } = useNotification();
  const { mutate: getExistsMenu } = useGetExistsMenu();
  const [existsMenu, setExistsMenu] = useState(false);

  useEffect(() => {
    getExistsMenu(undefined, {
      onSuccess: (data) => {
        setExistsMenu(data?.data?.exists || false);
      },
    });
  }, []);

  const handleUploadExcelError = (errors: ErrorInterface[]) => {
    setIsError(true);
    setTitle(t('menuMaker:importMenu.dragAndDrop.title_error') as string);
    setDescription(t('menuMaker:importMenu.dragAndDrop.description_error') as string);
    setIcon(ErrorIcon);
    setErrorList(errors);
  };

  const handleUploadExcelSuccess = (file: File) => {
    setTitle(t('menuMaker:importMenu.dragAndDrop.title_success') as string);
    setDescription(t('menuMaker:importMenu.dragAndDrop.description_success') as string);
    setIcon(SuccessIcon);
    setIsSuccess(true);
    setExcelFile(file);
  };

  const handleDownloadTemplate = (type: DownloadTemplateOptions['type']) => {
    downloadTemplate(
      { type },
      {
        onSuccess: (res) => {
          const url = window.URL.createObjectURL(res);
          const a = document.createElement('a');
          a.href = url;
          a.download =
            type === 'EMPTY'
              ? `${t('menuMaker:emptyMenus.draft.emptyTemplateFileName')}.xlsx`
              : `${t('menuMaker:emptyMenus.draft.exampleTemplateFileName')}.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);
        },
        onError: () => {
          setNotification(t('menuMaker:emptyMenus.draft.downloadButtonError'), { variant: 'error' });
        },
      },
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      {isError && !isSuccess && (
        <FeedbackBanner
          description={t('menuMaker:importMenu.dragAndDrop.error_message')}
          title={
            errorList.length === 1
              ? t('menuMaker:importMenu.dragAndDrop.error_title_one', { count: errorList.length })
              : t('menuMaker:importMenu.dragAndDrop.error_title_other', { count: errorList.length })
          }
          type="error"
        />
      )}
      {existsMenu && (
        <FeedbackBanner
          description={<Trans i18nKey="menuMaker:importMenu.dragAndDrop.info_message_exists" />}
          type="warning"
        />
      )}
      {!isError && !isSuccess && (
        <FeedbackBanner description={t('menuMaker:importMenu.dragAndDrop.info_message')} type="info" />
      )}

      <DragAndDrop
        description={description}
        img={icon}
        onError={handleUploadExcelError}
        onSuccess={handleUploadExcelSuccess}
        title={title}
      />
      {isSuccess && (
        <Box>
          <Button
            color="primary"
            disabled={!excelFile}
            onClick={() => {
              if (excelFile) {
                onSuccess(excelFile);
              }
            }}
            size="large"
            sx={{ textTransform: 'none' }}
            variant="contained"
          >
            {t('menuMaker:importMenu.buttons.next')}
          </Button>
        </Box>
      )}
      {isError && !isSuccess && <ErrorTable errors={errorList} />}
      {!isError && !isSuccess && (
        <Box
          sx={{
            marginBlockStart: '2rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">{t('menuMaker:importMenu.dragAndDrop.template_title')}</Typography>
          <ButtonDownload
            color="primary"
            onClick={() => {
              handleDownloadTemplate('EMPTY');
            }}
            size="large"
            sx={{ textTransform: 'none' }}
            variant="outlined"
          >
            {t('menuMaker:importMenu.dragAndDrop.template_button')}
          </ButtonDownload>
          <Button
            color="secondary"
            component={Link}
            onClick={() => {
              handleDownloadTemplate('EXAMPLE');
            }}
            style={{ textDecoration: 'underline' }}
          >
            {t('menuMaker:importMenu.dragAndDrop.template_link')}
          </Button>
        </Box>
      )}
    </Box>
  );
};
